import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { Card, CardSection } from 'components/Card';
import { Form } from 'components/HookForm';
import Label from 'components/HookForm/Label/Label';
import Message from 'components/Message';

import { MANAGE_SERVICES_TAB } from 'constants/logic';

import { BackNavLink, Title } from 'features/App';

import { bem } from 'helpers/class-name';
import { toMajorUnits } from 'helpers/currency-symbol';
import { reportError } from 'helpers/error-logging';
import { makePath } from 'helpers/link-generation';

import { selectActiveVenue } from 'selectors/general';

import { refreshVenueClasses } from '../ClassList';
import ServicePricingSection, {
  PRICING_MODE,
  messages as pricingSectionMessages,
} from '../Components/ServicePricingSection';
import { blocksEditing } from '../Components/PricingStatusStepper';
import FooterForm from '../FooterForm/FooterFormContainer';
import useIntegratedService from '../hooks/useIntegratedService';
import { messages as servicesMessages } from '../Services/Services.messages';
import {
  getNotListedOnline,
  openPriceSubmitErrorModal,
} from '../Services/ServiceForm/PriceSubmitErrorModal/priceSubmitErrorModal.helpers';

import { messages } from './IntegratedServiceDetail.messages';

import './IntegratedServiceDetail.scss';

const { block, element } = bem('integrated-service-detail');

// Detail/edit page for integrated (appointment-type) partners: read-only class
// fields plus the pricing-request status timeline. Pricing is editable only when
// there's no request yet, or the last one was rejected (action required).
const IntegratedServiceDetail = ({ venueId, classId, className, isClassEnabled }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pricingRef = useRef(null);
  const venue = useSelector(selectActiveVenue);

  const { service, isLoading } = useIntegratedService(venueId, classId);

  // The class is live, or it already has at least one ClassPass rate.
  const rules = service?.pricing_rules;
  const hasPrice = !!(rules && (rules.floor_rate || rules.drop_in_rate || rules.static_rate));
  const isAlreadyBookable = isClassEnabled || hasPrice;

  // Read-only only while a request is mid-review (pending) or approved. No
  // request, a rejected one, or a terminal/unknown status (cancelled, handoff)
  // stays editable so the partner always has a path to act.
  const retailRateRequest = service?.retail_rate_request;
  const isEditable = !blocksEditing(retailRateRequest);
  const pricingMode = isEditable ? PRICING_MODE.INPUT : PRICING_MODE.READONLY;

  // Already priced with no request: nothing to submit, so hide the section.
  const showPricingSection = !!retailRateRequest || !hasPrice;
  const submittedPrice = retailRateRequest?.retail_rate
    ? toMajorUnits(retailRateRequest.retail_rate.currency_code, retailRateRequest.retail_rate.currency_units)
    : undefined;

  const backUrl = makePath.manageServices({ venueId, tab: MANAGE_SERVICES_TAB.SERVICES });

  const handleCancel = () => navigate(backUrl);

  const handleSubmit = async (formValues, formAsyncFns) => {
    const { resolveSubmit, rejectSubmit } = formAsyncFns;

    try {
      const fileProofs = await pricingRef.current.collectProofs();
      const proofCheck = pricingRef.current.validateProof({ formValues, fileProofs });
      if (!proofCheck.valid) {
        dispatch(createErrorBanner(proofCheck.error));
        return rejectSubmit();
      }

      try {
        await pricingRef.current.submitPricing({ classId, formValues, fileProofs });
      } catch (error) {
        reportError('Error submitting integrated pricing request', { error });
        openPriceSubmitErrorModal(dispatch, {
          currencyCode: venue.currencyCode,
          serviceName: service?.name || className,
          linkToPrice: formValues.linkToPrice,
          servicePrice: formValues.servicePrice,
          notListedOnline: getNotListedOnline(formValues, { hasFileProof: fileProofs.length > 0 }),
        });
        return rejectSubmit();
      }

      dispatch(createInfoBanner(messages.pricingSubmitted));
      // The list is only fetched on route enter and going back isn't one, so
      // refetch now or the service keeps its old status until a hard refresh.
      dispatch(refreshVenueClasses());
      resolveSubmit();
      return navigate(backUrl);
    } catch (error) {
      reportError('Proof file upload failed before integrated pricing submit', { error });
      dispatch(createErrorBanner(servicesMessages.error.problemWithRequest));
      return rejectSubmit();
    }
  };

  const serviceName = service?.name || className;
  const { description, duration_minutes: durationMinutes } = service || {};

  return (
    <>
      <BackNavLink to={backUrl} />
      <Message as={Title} message={serviceName} />

      {isLoading ? (
        <Card loading />
      ) : (
        <Form id="integratedServiceForm" onSubmit={handleSubmit} onReset={handleCancel} className={block()}>
          {({ formState, watch }) => {
            const { isSubmitting } = formState;
            const servicePrice = watch('servicePrice');
            const accountabilityAgreement = watch('accountabilityAgreement');
            const submitDisabled = !servicePrice || !accountabilityAgreement;

            return (
              <Card flush className={element('card')}>
                <CardSection>
                  <Label message={messages.name} />
                  <p className={element('field-value')} data-cypress="integrated-service-name">
                    {serviceName}
                  </p>

                  {description && (
                    <>
                      <Label message={messages.description} />
                      <p className={element('field-value')} data-cypress="integrated-service-description">
                        {description}
                      </p>
                    </>
                  )}

                  {!!durationMinutes && (
                    <>
                      <Label message={messages.duration} />
                      <Message
                        as="p"
                        className={element('field-value')}
                        message={messages.durationValue}
                        values={{ duration: durationMinutes }}
                        data-cypress="integrated-service-duration"
                      />
                    </>
                  )}

                  {showPricingSection && (
                    <ServicePricingSection
                      ref={pricingRef}
                      mode={pricingMode}
                      currentPrice={submittedPrice}
                      priceLabel={pricingSectionMessages.submittedPrice}
                      priceTooltip={pricingSectionMessages.submittedPriceTooltip}
                      prefillEnabled={false}
                      isAlreadyBookable={isAlreadyBookable}
                      retailRateRequest={retailRateRequest}
                    />
                  )}
                </CardSection>

                {showPricingSection && isEditable && (
                  <FooterForm
                    submitMessage={messages.submitForReview}
                    formName="integratedServiceForm"
                    visible
                    submitDisabled={submitDisabled}
                    submitting={isSubmitting}
                    onClickCancel={handleCancel}
                  />
                )}
              </Card>
            );
          }}
        </Form>
      )}
    </>
  );
};

IntegratedServiceDetail.propTypes = {
  venueId: PropTypes.number,
  classId: PropTypes.number,
  className: PropTypes.string,
  isClassEnabled: PropTypes.bool,
};

export default IntegratedServiceDetail;
